import { Trans, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Film } from 'lucide-react';
import MediaSubjectPicker, { MediaSubjectItem } from 'modules/championship/ui/components/form-parts/media/media-subject-picker';
import MediaSubjectPickerSkeleton from 'modules/championship/ui/components/form-parts/media/media-subject-picker-skeleton';
import { mediaSubjectMatchDate } from 'modules/manager/domain/media-kind';
import { useChampionshipMediaSubjectsQuery } from 'modules/manager/store/media-generation-jobs-api';
import { ManualGoalPairing, ReviewClip, ReviewClipKind, ReviewPlatesMode } from 'modules/match-video/domain/interfaces';
import { createBuiltInFrameProvider, createReviewFrameProvider, toReviewMatchContext } from 'modules/match-video/domain/render';
import {
  addReviewClips,
  buildReviewTimeline,
  matchGoalsToClips,
  moveReviewClip,
  parseClipName,
  removeReviewClip,
  setReviewClipKind,
} from 'modules/match-video/domain/utils';
import { useReviewMatchQuery } from 'modules/match-video/store';
import { useReviewAssembly, useVideoTemplateGroups } from 'modules/match-video/ui/hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDEmptyState from 'ud-ui/components/empty-state';
import { SkeletonPreview } from 'ud-ui/components/skeleton';
import UDText from 'ud-ui/components/text';
import BuildProgress from './build-progress';
import ClipList from './clip-list';
import ClipNamingHint from './clip-naming-hint';
import { ClipSourcePicker } from './clip-source-picker';
import VideoSetPicker from './video-set-picker';

/** `?matchId=` — the match chosen on this tab or handed over by «Собрать обзор» on the match «Моменты». */
export const MATCH_VIDEO_MATCH_PARAM = 'matchId';

// Name and size: the same file added twice is skipped (`addReviewClips`), so the id is unique within the list.
const toClip = (file: File): ReviewClip => ({
  id: `${file.name}:${file.size}`,
  name: file.name,
  source: { type: 'file', file },
  ...parseClipName(file.name),
});

interface SectionProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  testId: string;
}

const Section = ({ title, subtitle, children, testId }: SectionProps) => (
  <UDCard as="section" variant="plain" padding="lg" className="flex flex-col gap-4" data-testid={testId}>
    <div>
      <UDText type="title-3">{title}</UDText>
      {subtitle && (
        <UDText type="subhead" className="text-inkSecondary">
          {subtitle}
        </UDText>
      )}
    </div>
    {children}
  </UDCard>
);

interface MatchVideoTabProps {
  championshipId: number;
  /** Organization of the championship (`championship.tournamentId`) — its template groups are offered as sets. */
  organizerId: number;
}

/**
 * «Видео» on the championship «Медиа» tab: a match review assembled in the browser from ready clips (IMGM-1606).
 * Match → clips → set → «Собрать» → «Скачать»; nothing is uploaded or kept, the file lives until it is downloaded.
 */
const MatchVideoTab = ({ championshipId, organizerId }: MatchVideoTabProps) => {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const matchId = Number(searchParams.get(MATCH_VIDEO_MATCH_PARAM)) || null;

  const subjects = useChampionshipMediaSubjectsQuery(championshipId);
  const matchQuery = useReviewMatchQuery({ matchId: matchId ?? 0, championshipId }, { skip: matchId === null });
  const details = matchId !== null && matchQuery.data?.id === matchId ? matchQuery.data : undefined;
  const groups = useVideoTemplateGroups(championshipId, organizerId);
  const assembly = useReviewAssembly();
  const { reset } = assembly;

  const [clips, setClips] = useState<ReviewClip[]>([]);
  // Built-in sets have both plates; a template group — the video plate templates it holds.
  const hasPlates = groups.builtInId !== null || Object.keys(groups.plates).length > 0;
  const [plates, setPlates] = useState<ReviewPlatesMode>(hasPlates ? 'set' : 'none');
  // A set with plates starts with them on, one without — off.
  useEffect(() => {
    setPlates(hasPlates ? 'set' : 'none');
  }, [hasPlates]);

  const matchItems: MediaSubjectItem[] = useMemo(
    () =>
      (subjects.data?.matches ?? []).map((match) => {
        const date = mediaSubjectMatchDate(match.date);
        return {
          id: match.id,
          title: `${match.teams[0]?.name ?? '—'} — ${match.teams[1]?.name ?? '—'}`,
          group: match.round?.name,
          meta: date ? format(date, 'dd.MM.yyyy HH:mm') : undefined,
        };
      }),
    [subjects.data],
  );

  // A ready review belongs to its match and set; a new choice drops it, so a stale file is never downloaded.
  useEffect(() => {
    reset();
  }, [matchId, groups.selectedId, groups.builtInId, reset]);

  const selectMatch = useCallback(
    (id: number) => {
      const next = new URLSearchParams(searchParams);
      next.set(MATCH_VIDEO_MATCH_PARAM, String(id));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Goals chosen by hand belong to the goals of this match: another match starts from the order again.
  const [manualGoals, setManualGoals] = useState<ManualGoalPairing>({});
  useEffect(() => {
    setManualGoals({});
  }, [matchId]);
  const [skipped, setSkipped] = useState(0);

  // Every change of the clips or their goals makes a ready review stale, so each handler drops it first.
  // Clips from either source go into one list; a second pick adds to it instead of replacing it.
  const handlePick = useCallback(
    (files: File[]) => {
      reset();
      const added = addReviewClips(clips, files.map(toClip));
      setClips(added.clips);
      setSkipped(added.skipped);
    },
    [clips, reset],
  );
  const handleGoalChange = useCallback(
    (clipId: string, eventId: number | null) => {
      reset();
      setManualGoals((current) => ({ ...current, [clipId]: eventId }));
    },
    [reset],
  );
  const handleKindChange = useCallback(
    (clipId: string, kind: ReviewClipKind) => {
      reset();
      setClips((current) => setReviewClipKind(current, clipId, kind));
    },
    [reset],
  );
  const handleMove = useCallback(
    (clipId: string, toIndex: number) => {
      reset();
      setClips((current) => moveReviewClip(current, clipId, toIndex));
    },
    [reset],
  );
  const handleRemove = useCallback(
    (clipId: string) => {
      reset();
      setSkipped(0);
      setClips((current) => removeReviewClip(current, clipId));
    },
    [reset],
  );
  // The files and the review made of them go away together, so their memory is released.
  const handleClear = useCallback(() => {
    reset();
    setSkipped(0);
    setManualGoals({});
    setClips([]);
  }, [reset]);

  const context = useMemo(() => (details ? toReviewMatchContext(details) : null), [details]);
  const teamNames = useMemo(
    () =>
      context
        ? new Map([
            [context.match.homeTeam.id, context.match.homeTeam.name],
            [context.match.awayTeam.id, context.match.awayTeam.name],
          ])
        : undefined,
    [context],
  );
  const goalMatch = useMemo(
    () => (details ? matchGoalsToClips(details.events, clips, manualGoals) : null),
    [details, clips, manualGoals],
  );
  const timeline = useMemo(() => buildReviewTimeline(clips), [clips]);

  const blockedReason = (() => {
    if (matchId === null) return t`Выберите матч.`;
    if (!context) return matchQuery.isError ? t`Матч не загрузился.` : t`Загружаем матч…`;
    if (clips.length === 0) return t`Добавьте клипы матча.`;
    if (groups.builtInId !== null) return null;
    if (groups.selectedId === null) return groups.isLoading ? t`Загружаем наборы…` : t`Нет набора шаблонов для заставки.`;
    if (!groups.intro) return groups.isGroupLoading ? t`Проверяем набор…` : t`В наборе нет шаблона для заставки.`;
    return null;
  })();

  const handleAssemble = () => {
    if (!context || !goalMatch) return Promise.resolve();
    if (groups.builtInId !== null) {
      const frames = createBuiltInFrameProvider({
        setId: groups.builtInId,
        match: context.match,
        players: context.players,
        goalByClipId: goalMatch.goalByClipId,
        options: { plates },
        locale: i18n.locale,
      });
      return assembly.assemble(timeline, frames);
    }
    if (!groups.intro) return Promise.resolve();
    const frames = createReviewFrameProvider({
      match: context.match,
      players: context.players,
      intro: groups.intro,
      plates: groups.plates,
      goalByClipId: goalMatch.goalByClipId,
      goals: goalMatch.goals,
      options: { plates },
    });
    return assembly.assemble(timeline, frames);
  };

  const isAssembling = assembly.status === 'assembling';
  const isMatchesLoading = subjects.isLoading;

  if (!isMatchesLoading && !subjects.isError && matchItems.length === 0) {
    return (
      <UDCard variant="plain" padding="lg" data-testid="match-video-tab">
        <UDEmptyState
          icon={<Film size={28} />}
          title={<Trans>В турнире пока нет матчей</Trans>}
          subtitle={<Trans>Обзор собирается для матча: добавьте матчи на вкладке «Этапы», и они появятся здесь.</Trans>}
          action={
            <UDButton
              variant="primary"
              type="button"
              onClick={() => navigate(Pages.CHAMPIONSHIPS.STEPS.builders.index(championshipId))}
            >
              <Trans>Открыть этапы</Trans>
            </UDButton>
          }
          className="py-10"
        />
      </UDCard>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6" data-testid="match-video-tab">
      <Section
        title={<Trans>Матч</Trans>}
        subtitle={<Trans>Из протокола берутся счёт, команды и авторы голов.</Trans>}
        testId="match-video-match"
      >
        {subjects.isError ? (
          <div className="flex flex-wrap items-center gap-3">
            <UDText type="subhead" role="alert" className="text-CaseErrorText">
              <Trans>Не удалось загрузить матчи турнира.</Trans>
            </UDText>
            <UDButton variant="secondary" type="button" onClick={subjects.refetch}>
              <Trans>Повторить</Trans>
            </UDButton>
          </div>
        ) : isMatchesLoading ? (
          <MediaSubjectPickerSkeleton subjectType="match" />
        ) : (
          <SkeletonPreview skeleton={<MediaSubjectPickerSkeleton subjectType="match" />}>
            <MediaSubjectPicker items={matchItems} value={matchId} onChange={selectMatch} label={t`Матч обзора`} />
          </SkeletonPreview>
        )}
        {matchQuery.isError && matchId !== null && (
          <div className="flex flex-wrap items-center gap-3">
            <UDText type="footnote" role="alert" className="text-CaseErrorText">
              <Trans>Не удалось загрузить протокол матча.</Trans>
            </UDText>
            <UDButton variant="secondary" type="button" onClick={matchQuery.refetch}>
              <Trans>Повторить</Trans>
            </UDButton>
          </div>
        )}
      </Section>

      <Section
        title={<Trans>Клипы</Trans>}
        subtitle={
          <Trans>
            Добавляйте файлы по одному или сразу несколько — они собираются в один список, его порядок и есть порядок обзора.
          </Trans>
        }
        testId="match-video-clips-section"
      >
        <ClipNamingHint />
        <ClipSourcePicker onPick={handlePick} disabled={isAssembling} />
        {clips.length > 0 && (
          <ClipList
            clips={clips}
            goalMatch={goalMatch}
            players={context?.players}
            teams={teamNames}
            manual={manualGoals}
            onGoalChange={handleGoalChange}
            onKindChange={handleKindChange}
            onMove={handleMove}
            onRemove={handleRemove}
            onClear={handleClear}
            skipped={skipped}
            disabled={isAssembling}
          />
        )}
      </Section>

      <Section
        title={<Trans>Оформление</Trans>}
        subtitle={
          groups.builtInId !== null ? (
            <Trans>Стандартный набор: анимированные заставка и плашки «Гол» и «Опасный момент».</Trans>
          ) : (
            <Trans>Заставка рисуется по шаблону матча из набора медиа-менеджера.</Trans>
          )
        }
        testId="match-video-design"
      >
        <VideoSetPicker groups={groups} plates={plates} onPlatesChange={setPlates} hasPlates={hasPlates} disabled={isAssembling} />
      </Section>

      <Section
        title={<Trans>Сборка</Trans>}
        subtitle={<Trans>Видео собирается в браузере и никуда не загружается: после скачивания оно есть только у вас.</Trans>}
        testId="match-video-build-section"
      >
        <BuildProgress assembly={assembly} blockedReason={blockedReason} onAssemble={handleAssemble} fileName={t`Видеообзор.mp4`} />
      </Section>
    </div>
  );
};

export default MatchVideoTab;
